import {
  createContext,
  type Dispatch,
  type MutableRefObject,
  type PropsWithChildren,
  type SetStateAction,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import {
  type MotionValue,
  useMotionValueEvent,
  useScroll,
  useSpring
} from 'framer-motion';

type HeaderUIValue = {
  gridlineHeight: number;
  isMobileOpen: boolean;
  navHeight: number;
  setIsMobileOpen: Dispatch<SetStateAction<boolean>>;
  smoothSquishProgress: MotionValue<number>;
  tickerHeight: number;
};

type HeaderUIRefsValue = {
  gridlineRef: MutableRefObject<HTMLDivElement | null>;
  navRef: MutableRefObject<HTMLDivElement | null>;
  tickerRef: MutableRefObject<HTMLDivElement | null>;
};

const HeaderUIContext = createContext<HeaderUIValue | null>(null);
const HeaderUIRefsContext = createContext<HeaderUIRefsValue | null>(null);

export function HeaderUIProvider(props: PropsWithChildren) {
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isScrolledToTop, setIsScrolledToTop] = useState(true);
  const { scrollY } = useScroll();
  useMotionValueEvent(scrollY, 'change', current => {
    setIsScrolledToTop(current < 40);
  });

  const [navHeight, setNavHeight] = useState(60);
  const [gridlineHeight, setGridlineHeight] = useState(1.25 * 16);
  const [tickerHeight, setTickerHeight] = useState(28);

  const gridlineRef = useRef<HTMLDivElement | null>(null);
  const navRef = useRef<HTMLDivElement | null>(null);
  const tickerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const gridlineNode = gridlineRef.current;
    const tickerNode = tickerRef.current;
    const navNode = navRef.current;
    const nodes = [gridlineNode, tickerNode, navNode];

    const cleanups: Array<() => void> = [];
    const observer = new ResizeObserver(() => {
      if (gridlineNode)
        setGridlineHeight(gridlineNode.getBoundingClientRect().height);
      if (tickerNode)
        setTickerHeight(tickerNode.getBoundingClientRect().height);
      if (navNode) setNavHeight(navNode.getBoundingClientRect().height);
    });

    nodes.forEach(node => {
      if (!node) return;
      observer.observe(node);
      cleanups.push(() => {
        observer.unobserve(node);
      });
    });

    return () => {
      cleanups.forEach(cleanup => {
        cleanup();
      });
    };
  }, []);

  const smoothSquishProgress = useSpring(0, { bounce: 0, duration: 400 });
  useEffect(() => {
    const isSquished = !isScrolledToTop || isMobileOpen;
    smoothSquishProgress.set(isSquished ? 1 : 0);
  }, [isMobileOpen, isScrolledToTop, smoothSquishProgress]);

  return (
    <HeaderUIContext.Provider
      value={{
        navHeight,
        gridlineHeight,
        tickerHeight,
        smoothSquishProgress,
        isMobileOpen,
        setIsMobileOpen
      }}>
      <HeaderUIRefsContext.Provider
        value={useMemo(
          () => ({
            gridlineRef,
            navRef,
            tickerRef
          }),
          []
        )}>
        {props.children}
      </HeaderUIRefsContext.Provider>
    </HeaderUIContext.Provider>
  );
}

export function useHeaderUI() {
  const value = useContext(HeaderUIContext);

  if (value === null) {
    throw new Error('must call useHeaderUI() within <HeaderUIProvider />');
  }

  return value;
}

export function useHeaderUIRefs() {
  const value = useContext(HeaderUIRefsContext);

  if (value === null) {
    throw new Error(
      'must call HeaderUIRefsContext() within <HeaderUIProvider />'
    );
  }

  return value;
}
